/**
 * Chip component — shared types and Zod prop schema.
 *
 * Chip is a compact element for selections, filters, and tags.
 * Variants: default / outline / filled, with selected and disabled states.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { IconNameEnum } from '../Icon/Icon';

export const ChipVariant = z.enum(['Default', 'Outline', 'Filled', 'Subtle']);
export type ChipVariant = z.infer<typeof ChipVariant>;

export const ChipSize = z.enum(['S', 'M', 'L']);
export type ChipSize = z.infer<typeof ChipSize>;
export const ChipIconPosition = z.enum(['leading', 'trailing']);
export type ChipIconPosition = z.infer<typeof ChipIconPosition>;

/**
 * Chip prop schema for the catalog (ADR-011).
 *
 * `closable` is a visual indicator — when true, the chip renders a close icon.
 * The `onClose` callback is a runtime prop defined on platform-specific interfaces
 * (`NativeChipProps`, `WebChipProps`), not in this catalog schema. Catalog schemas
 * define data/variant props only; callbacks are platform-specific (ADR-011 convention).
 *
 * At runtime, the close icon is only interactive when both `closable === true` AND
 * `onClose` is provided. Passing `closable: true` without `onClose` renders the
 * icon as decorative (no press handler).
 */
export const chipPropsSchema = z.object({
  label: z.string(),
  variant: ChipVariant.default('Default'),
  size: ChipSize.default('M'),
  selected: z.boolean().default(false),
  disabled: z.boolean().default(false),
  closable: z.boolean().default(false),
  iconName: IconNameEnum.optional(),
  iconPosition: ChipIconPosition.default('leading'),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type ChipProps = z.infer<typeof chipPropsSchema>;

export const chipGroupPropsSchema = z.object({
  options: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      iconName: IconNameEnum.optional(),
    }),
  ),
  selected: z.union([z.string(), z.array(z.string())]),
  multiple: z.boolean().default(false),
  size: ChipSize.default('M'),
});

export type ChipGroupProps = z.infer<typeof chipGroupPropsSchema>;

/** Colour triplet per variant × state. Single source of truth. */
interface ChipColors {
  background: string;
  text: string;
  border: string;
}

export function chipColorsFor(
  variant: ChipVariant,
  selected: boolean,
  disabled: boolean,
): ChipColors {
  if (disabled) {
    return {
      background: colors.background.secondary,
      text: colors.text.disabled,
      border: 'transparent',
    };
  }

  if (selected) {
    return {
      background: colors.button.primary,
      text: colors.text.inverted,
      border: colors.button.primary,
    };
  }

  switch (variant) {
    case 'Outline':
      return {
        background: 'transparent',
        text: colors.text.primary,
        border: colors.stroke.tertiary,
      };
    case 'Filled':
      return {
        background: colors.background.secondary,
        text: colors.text.primary,
        border: 'transparent',
      };
    case 'Subtle':
      // A borderless, low-contrast pill on the light-grey surface — used for
      // read-only metadata pills (e.g. the check-in card's "4 min" chips).
      // Uses `background.primary` (#f2f2f2): the design reference pins that hex;
      // `background.secondary` is #e3e3e3, so the hex — not the prose token
      // name — is authoritative here.
      return {
        background: colors.background.primary,
        text: colors.text.primary,
        border: 'transparent',
      };
    case 'Default':
    default:
      return {
        background: colors.background.light,
        text: colors.text.primary,
        border: colors.stroke.tertiary,
      };
  }
}

/** Typography style for each chip size. */
export const typographyForChipSize: Record<ChipSize, (typeof typography)[keyof typeof typography]> =
  {
    S: typography.c1Regular,
    M: typography.b2Regular,
    L: typography.button2,
  };

/** Padding for each chip size. */
export const paddingForChipSize: Record<ChipSize, { vertical: number; horizontal: number }> = {
  S: { vertical: spacing.xs, horizontal: spacing.s },
  M: { vertical: spacing.s, horizontal: spacing.m },
  L: { vertical: spacing.s + spacing.xxs, horizontal: spacing.m },
};

/** Icon size for each chip size. */
export const iconSizeForChipSize: Record<ChipSize, number> = {
  S: 14,
  M: 18,
  L: sizing.icon.xxs,
};

/**
 * The close (✕) affordance is a real >=48dp layout box (ADR-025 Rule 5).
 * hitSlop cannot satisfy the rule here: React Native never extends a hit
 * area past the parent's bounds, and the compact pill is shorter than the
 * touch-target minimum — so the closable pill grows to the target height
 * instead (padding-style expansion, the rule's preferred mechanism for
 * >8dp). Non-closable chips keep the compact geometry.
 */
export const CHIP_CLOSE_TARGET_SIZE = sizing.touchTarget.minimum;

/**
 * Outline stroke of a bordered chip — the shared default border weight, not a
 * chip-specific value. Design-system-internal (it is not on the public barrel):
 * a consumer that wants this weight reads `states.borderWidth.default`, so
 * nothing outside the package depends on a chip to learn a generic stroke.
 */
export const CHIP_BORDER_WIDTH = states.borderWidth.default;

/** CAB's large metadata pill height. */
export const CHIP_L_HEIGHT = spacing.xxl + spacing.xs;

/** Border radius for chips — always pill-shaped. */
export const CHIP_BORDER_RADIUS = radius.rounded;

/** Gap between icon/close and label. */
export const CHIP_ICON_GAP = spacing.xs;
